import { useParams } from "react-router-dom";
import UnlimitBreadcrumb from "../../../components/ui-kit/unlimit-breadcrumb";
import httpService from "../../../services/http/peoples/shoppers.service";
import salesService from "../../../services/http/sales/sales.service";
import { useEffect, useState } from "react";
import UnlimitLoading from "../../../components/ui-kit/unlimit-loading";
import UnlimitFieldDetails from "../../../components/ui-kit/unlimit-field-details";
import UnlimitTable from "../../../components/ui-kit/unlimit-table";
import usePagination, {
  createInitialPagination,
} from "../../../custom-hooks/usePagination";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import moment from "moment";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useRoleAndPerm from "../../../utils/permissionFunction";
import { useSelectedCountry } from "../../../feature-module/dashboard/hooks/useSelectedCountry";
import formatNumber from "../../../utils/numberFunction";
import UnlimitToggle from "../../../components/ui-kit/unlimit-toggle-2";
import { Collapse } from "antd";
import notificationService from "../../../services/toaster.service";
import { hydrateErrors } from "../../../utils/error.utils";
import { useForm } from "react-hook-form";

import "./shopper-details.css";
import { render } from "@react-three/fiber";
const { Panel } = Collapse;

export default function ShoppersDetails() {
  const { hasPermission } = useRoleAndPerm();
  const [pageLoading, setPageLoading] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const { id } = useParams();
  const [shopper, setShopper] = useState(null);
  const { t } = useTranslation();
  const [companyAccounts, setCompanyAccounts] = useState([]);
  const [salesOrderHistory, setSaleOrderHistory] = useState([]);
  const initialPagination = createInitialPagination(t);
  const { setError } = useForm();
  const [isShopperApproved, setIsShopperApproved] = useState(false);

  const [statusToggleLoading, setStatusToggleLoading] = useState(false);
  const [updateAssociateLoading, setUpdateAssociateLoading] = useState(false);
  const [approvalLoadings, setApprovalLoadings] = useState({}); // {accountId: loading}
  const [accountToggleLoadings, setAccountToggleLoadings] = useState({}); // {accountId: loading}
  const [shopToggleLoadings, setShopToggleLoadings] = useState({}); // {accountId_shopId: loading}
  const { paginationParams, updatePaginationParams } =
    usePagination(initialPagination);
  const [loading, setLoading] = useState(false);

  const getShopper = async () => {
    try {
      setPageLoading(true);
      const response = await httpService.getShopper(id);
      if (response && response.status === 200) {
        setShopper(response.data.data);
        setIsActive(
          response.data.data.is_active === "1" ||
            response.data.data.is_active === 1 ||
            response.data.data.is_active === true
        );

        //disabled company account status update button until shopper is approved
        if (response.data.data && response.data.data.status == "approved") {
          setIsShopperApproved(true);
        }

        getShopperAccount(response.data.data.id);
      }
    } catch (error) {
      console.error("Error fetching shopper:", error);
      notificationService.errorMessage("Failed to fetch shopper details");
    } finally {
      setPageLoading(false);
    }
  };

  const getShopperAccount = async (shopperId) => {
    try {
      const response = await httpService.getShopperAccount(shopperId || id);
      if (response && response.status === 200) {
        if (response.data.data && response.data.data.company_accounts) {
          setCompanyAccounts(response.data.data.company_accounts);
        }
      }
    } catch (error) {
      console.error("Error fetching shopper accounts:", error);
      notificationService.errorMessage("Failed to fetch shopper accounts");
    }
  };

  const getSalesOrderHistory = async (
    pagination = paginationParams.pagination,
    shopperId = id
  ) => {
    const data = {
      page: pagination?.current ?? 1,
      per_page: pagination?.pageSize ?? 10,
      shopper_id: shopperId,
    };
    setLoading(true);

    const response = await salesService
      .salesList(data)
      .then((response) => {
        if (response.status == 200) {
          setSaleOrderHistory(response.data.data?.data);
        }
        updatePaginationParams({
          pagination: {
            ...paginationParams.pagination,
            current: response.data.data.current_page,
            total: response.data.data.total,
            pageSize: response.data.data.per_page,
          },
        });
      })
      .catch((error) => {
        console.error("Error:", error);
        updatePaginationParams({
          pagination: {
            ...paginationParams.pagination,
          },
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    if (!pagination) {
      pagination = initialPagination;
    }
    await updatePaginationParams({
      pagination,
      sorter,
    });
    await getSalesOrderHistory(pagination, id);
  };

  useEffect(() => {
    if (id) {
      getShopper();
      getShopperAccount();
      getSalesOrderHistory();
    }
  }, [id]);

  const breadcrumbItems = [
    { label: "", link: "/", icon: "feather-home" },
    { label: t("shopper.shopper"), link: "/shoppers-list" },
    { label: t("shopper.viewShopper"), active: true },
  ];

  const columns = [
    {
      title: t("tableColumnName.srNo"),
      dataIndex: "index",
      align: "center",
      render: (value, item, index) =>
        (paginationParams?.pagination?.current - 1) *
          paginationParams?.pagination?.pageSize +
        (index + 1),
    },
    {
      title: t("Sales.SalesID"),
      dataIndex: "order_code",
      render: (text, record) => {
        return record?.order_code ?? "";
      },
    },
    {
      title: t("tableColumnName.lmAccountNo"),
      dataIndex: "lm_account_no",
      render: (text, record) => {
        return record?.company_account?.lm_account_number ?? "";
      },
    },
    {
      title: t("tableColumnName.reference"),
      dataIndex: "order_code",
    },
    {
      title: t("logs.date"),
      dataIndex: "created_at",
      render: (text) => {
        return text ? moment(text).format("DD MMM YYYY - HH:mm:ss") : "";
      },
    },
    {
      title: t("shop.shop"),
      dataIndex: "outlet",
      hidden: false,
      render: (text, record) => (
        <div className="d-flex align-items-center">
          <ImageWithBasePath
            type="link"
            src={record.outlet?.image_url}
            alt="Contact"
            style={{ width: 30, height: 30, marginRight: 10 }}
          />
          {record?.outlet?.name}
        </div>
      ),
    },
    {
      title: t("tableColumnName.actions"),
      key: "actions",
      hidden: !hasPermission(["sale-view"]),
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            {hasPermission(["sale-view"]) && (
              <Link
                to={`/sales/${record.id}/details`}
                className="me-2 edit-icon p-2"
              >
                <i className="feather-eye"></i>
              </Link>
            )}
          </div>
        </div>
      ),
    },
  ];

  const toggleShopperStatus = async () => {
    try {
      setStatusToggleLoading(true);

      const data = {
        id: id,
        title: shopper.title,
        first_name: shopper.first_name,
        last_name: shopper.last_name,
        email: shopper.email,
        phone: shopper.phone,
        company_id: shopper.company.id,
        is_active: !isActive ? 1 : 0,
      };

      const response = await httpService
        .updateShopper(data)
        .then(async (response) => {
          if (response.status === 200) {
            setIsActive(!isActive);
            notificationService.successMessage(
              `Shopper ${!isActive ? "activated" : "deactivated"} successfully`
            );
          }
        })
        .catch((error) => {
          if (error.validation_error?.errors) {
            hydrateErrors(error.validation_error.errors, setError);
          }
        });
    } catch (error) {
      console.error("Error updating shopper status:", error);
      notificationService.errorMessage("Failed to update shopper status");
    } finally {
      setStatusToggleLoading(false);
    }
  };

  // Updated toggleAccountStatus function to also toggle all associated shops
  const toggleAccountStatus = async (accountId, newStatus) => {
    try {
      setAccountToggleLoadings((prev) => ({ ...prev, [accountId]: true }));

      console.log("Shopper ID:", accountId);
      console.log(`Toggling account ${accountId} status to ${newStatus}`);

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Update the local state to reflect the changes
      setCompanyAccounts((prevAccounts) => {
        return prevAccounts.map((account) => {
          if (account.id === accountId) {
            // Update the account status
            const updatedAccount = {
              ...account,
              is_account_associated_with_shopper: newStatus,
            };

            // If account is toggled on/off, also toggle all its shops accordingly
            if (updatedAccount.shops && updatedAccount.shops.length > 0) {
              updatedAccount.shops = updatedAccount.shops.map((shop) => ({
                ...shop,
                is_shop_associated_with_shopper: newStatus,
              }));
            }

            return updatedAccount;
          }
          return account;
        });
      });
    } catch (error) {
      console.error("Error updating account status:", error);
      notificationService.errorMessage("Failed to update account status");
    } finally {
      setAccountToggleLoadings((prev) => ({ ...prev, [accountId]: false }));
    }
  };

  // Updated toggleShopStatus function to also update parent account status if needed
  const toggleShopStatus = async (accountId, shopId, newStatus) => {
    try {
      const toggleKey = `${accountId}_${shopId}`;
      setShopToggleLoadings((prev) => ({ ...prev, [toggleKey]: true }));

      console.log(
        `Toggling account ${accountId}, shop ${shopId} status to ${newStatus}`
      );

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 600));

      // Update the local state to reflect the changes
      setCompanyAccounts((prevAccounts) => {
        return prevAccounts.map((account) => {
          if (account.id === accountId) {
            // Update the shop status
            const updatedShops = account.shops.map((shop) => {
              if (shop.id === shopId) {
                return {
                  ...shop,
                  is_shop_associated_with_shopper: newStatus,
                };
              }
              return shop;
            });

            let accountStatus = account.is_account_associated_with_shopper;

            if (newStatus) {
              accountStatus = true;
            } else {
              const allShopsOff = updatedShops.every(
                (shop) => !shop.is_shop_associated_with_shopper
              );
              if (allShopsOff && updatedShops.length > 0) {
                accountStatus = false;
              }
            }

            return {
              ...account,
              shops: updatedShops,
              is_account_associated_with_shopper: !newStatus
                ? account.is_account_associated_with_shopper
                : newStatus,
            };
          }
          return account;
        });
      });

      notificationService.successMessage(
        `Shop ${newStatus ? "associated" : "disassociated"} successfully`
      );

      // In real application, make API call here to sync the changes with backend
      // await httpService.updateShopAssociation(accountId, shopId, newStatus);
    } catch (error) {
      console.error("Error updating shop status:", error);
      notificationService.errorMessage("Failed to update shop status");
    } finally {
      const toggleKey = `${accountId}_${shopId}`;
      setShopToggleLoadings((prev) => ({ ...prev, [toggleKey]: false }));
    }
  };

  const handleApproval = async (accountId, isApproved) => {
    try {
      setApprovalLoadings((prev) => ({ ...prev, [accountId]: true }));

      console.log(
        `Account ${accountId} ${isApproved ? "approved" : "rejected"}`
      );

      const response = await httpService.associateShopperAccounts(id, {
        account_id: accountId,
        status: isApproved ? "approved" : "rejected",
      });

      setIsShopperApproved(isApproved ? true : false);

      if (response && response.status === 200) {
        await getShopperAccount(response.data.data.id);
        notificationService.successMessage(
          `Account ${isApproved ? "approved" : "rejected"} successfully`
        );
      }
    } catch (error) {
      console.error("Error updating approval status:", error);
      notificationService.errorMessage("Failed to update approval status");
    } finally {
      setApprovalLoadings((prev) => ({ ...prev, [accountId]: false }));
    }
  };

  const onClickUpdateAssociateAccount = async () => {
    try {
      setUpdateAssociateLoading(true);

      console.log("Update associated accounts clicked");
      const data = companyAccounts
        .filter((account) => account.is_account_associated_with_shopper)
        .map((account) => ({
          account_id: account.id,
          shops: account.shops
            .filter((shop) => shop.is_shop_associated_with_shopper)
            .map((shop) => shop.id),
        }));

      const response = await httpService.associateShopperAccountsUpdate(id, {
        accounts: data,
      });

      if (response && response.status === 200) {
        await getShopperAccount(response.data.data.id);
        notificationService.successMessage(
          "Associated accounts updated successfully"
        );
      }

      console.log("Data updated:", data);
    } catch (error) {
      console.error("Error updating associated accounts:", error);
      notificationService.errorMessage("Failed to update associated accounts");
    } finally {
      setUpdateAssociateLoading(false);
    }
  };

  // Helper function to get loading state for toggles
  const isToggleLoading = (type, accountId, shopId = null) => {
    switch (type) {
      case "account":
        return accountToggleLoadings[accountId] || false;
      case "shop":
        return shopToggleLoadings[`${accountId}_${shopId}`] || false;
      default:
        return false;
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex flex-column">
              <div className="page-title">
                <h3 className="text-black">{t("shopper.viewShopper")}</h3>
              </div>
              <UnlimitBreadcrumb items={breadcrumbItems} />
            </div>
          </div>

          {pageLoading ? (
            <div className="row d-flex justify-content-center">
              <UnlimitLoading width={50} height={50} />
            </div>
          ) : (
            shopper && (
              <>
                {/* Top section with shopper details */}
                <div className="row mb-4">
                  {/* Left card with shopper image and ID */}
                  <div className="col-md-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between mb-2">
                          <div className="company-logo mb-3">
                            <ImageWithBasePath
                              src={
                                shopper.company?.logo ||
                                "/assets/img/brand/siemens.png"
                              }
                              width={80}
                              height={50}
                              type="link"
                              alt="Company Logo"
                            />
                          </div>
                          <div>
                            <span
                              className={`badge ${
                                isActive ? "badge-success" : "badge-danger"
                              }`}
                            >
                              {isActive
                                ? t("general.actions.active")
                                : t("general.actions.inactive")}
                            </span>
                          </div>
                        </div>

                        <div className="text-center mb-3">
                          <div className="d-flex justify-content-center mb-3">
                            <ImageWithBasePath
                              src={
                                shopper.user?.image ||
                                "/assets/img/avatar/male-avatar.jpg"
                              }
                              width={100}
                              height={100}
                              className="rounded-circle"
                              alt="User Avatar"
                            />
                          </div>

                          <h4 className="mb-1">
                            {shopper.title} {shopper.first_name}{" "}
                            {shopper.last_name}
                          </h4>
                          <p className="text-muted">
                            LM User ID: {shopper.lm_shopper_number}
                          </p>
                        </div>

                        <div className="d-flex gap-2 mt-3">
                          {hasPermission(["shopper-edit"]) && (
                            <>
                              <Link
                                to={`/shoppers/${shopper.id}/edit`}
                                className="btn btn-secondary flex-grow-1"
                              >
                                {t("general.button.edit")}
                              </Link>
                              <button
                                onClick={toggleShopperStatus}
                                className={`btn ${
                                  isActive === true
                                    ? "btn-danger"
                                    : "btn-success"
                                } flex-grow-1`}
                                disabled={statusToggleLoading}
                              >
                                {statusToggleLoading ? (
                                  <div className="d-flex align-items-center justify-content-center">
                                    <div
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                    >
                                      <span className="visually-hidden">
                                        Loading...
                                      </span>
                                    </div>
                                    {t("general.loading")}
                                  </div>
                                ) : (
                                  <>
                                    {isActive === true
                                      ? t("general.button.deactivate")
                                      : t("general.button.activate")}
                                  </>
                                )}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right side with additional details */}
                  <div className="col-md-8">
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="row mb-3">
                          <div className="col-md-4">
                            <UnlimitFieldDetails
                              heading={t("inputField.label.email")}
                              details={shopper.email}
                            />
                          </div>
                          <div className="col-md-4">
                            <UnlimitFieldDetails
                              heading={t("inputField.label.phone")}
                              details={shopper.phone}
                            />
                          </div>
                          <div className="col-md-4">
                            <UnlimitFieldDetails
                              heading={t("inputField.label.associateAccounts")}
                              details={
                                shopper?.associate_accounts_count?.toString() ??
                                "0"
                              }
                            />
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-4">
                            <UnlimitFieldDetails
                              heading={t("inputField.label.createdBy")}
                              details={shopper.created_by?.name || "Admin"}
                            />
                          </div>
                          <div className="col-md-4">
                            <UnlimitFieldDetails
                              heading={t("inputField.label.createdAt")}
                              details={
                                shopper.created_at
                                  ? moment(shopper.created_at).format(
                                      "DD MMM YYYY - HH:mm:ss"
                                    )
                                  : "05 Jan 2025 - 14:47:00"
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom section with associate accounts */}
                <div className="card">
                  <div className="card-body">
                    <Collapse className="custom-collapse">
                      <Panel
                        header={t("shopper.associateAccounts")}
                        key="associateAccounts"
                      >
                        {hasPermission(["shopper-edit"]) && (
                          <div className="d-flex justify-content-end">
                            {isShopperApproved && (
                              <button
                                className="btn btn-primary mb-3"
                                onClick={onClickUpdateAssociateAccount}
                                disabled={updateAssociateLoading}
                              >
                                {updateAssociateLoading ? (
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                    >
                                      <span className="visually-hidden">
                                        Loading...
                                      </span>
                                    </div>
                                    {t("general.updating")}
                                  </div>
                                ) : (
                                  t("general.button.update")
                                )}
                              </button>
                            )}
                          </div>
                        )}
                        {companyAccounts.length > 0 ? (
                          companyAccounts.map((account, index) => (
                            <Collapse
                              className="account-collapse mb-2"
                              key={account.id}
                            >
                              <Panel
                                header={`Account-${index + 1} (${
                                  account.account_name
                                }, LM Account NO: ${
                                  account.lm_account_number
                                })`}
                                key={account.id}
                                extra={
                                  <div
                                    className="d-flex align-items-center gap-2"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {account.status === "pending" && (
                                      <>
                                        <button
                                          className={`btn-reject text-center`}
                                          onClick={() =>
                                            handleApproval(account.id, false)
                                          }
                                          disabled={
                                            approvalLoadings[account.id]
                                          }
                                          style={{
                                            minWidth: "25px",
                                            height: "25px",
                                            borderWidth: "50%",
                                            border: "none",
                                            color: "white",
                                          }}
                                        >
                                          {approvalLoadings[account.id] ? (
                                            <div
                                              className="spinner-border spinner-border-sm"
                                              role="status"
                                            >
                                              <span className="visually-hidden">
                                                Loading...
                                              </span>
                                            </div>
                                          ) : (
                                            <i className="feather-x"></i>
                                          )}
                                        </button>
                                        <button
                                          className={`btn-approve text-center`}
                                          onClick={() =>
                                            handleApproval(account.id, true)
                                          }
                                          disabled={
                                            approvalLoadings[account.id]
                                          }
                                          style={{
                                            minWidth: "26px",
                                            height: "26px",
                                            borderWidth: "50%",
                                            border: "none",
                                            color: "white",
                                          }}
                                        >
                                          {approvalLoadings[account.id] ? (
                                            <div
                                              className="spinner-border spinner-border-sm"
                                              role="status"
                                            >
                                              <span className="visually-hidden">
                                                Loading...
                                              </span>
                                            </div>
                                          ) : (
                                            <i className="feather-check"></i>
                                          )}
                                        </button>
                                      </>
                                    )}

                                    {hasPermission(["shopper-edit"]) && (
                                      <div className="status-toggle mt-2">
                                        <UnlimitToggle
                                          isActive={
                                            account.is_account_associated_with_shopper
                                          }
                                          id={`account_status_${account.id}`}
                                          toggleActive={(checked) =>
                                            toggleAccountStatus(
                                              account.id,
                                              checked
                                            )
                                          }
                                          disabled={isToggleLoading(
                                            "account",
                                            account.id
                                          )}
                                        />
                                      </div>
                                    )}
                                  </div>
                                }
                              >
                                <div className="shop-table">
                                  <table className="table">
                                    <thead>
                                      <tr>
                                        <th>{t("tableColumnName.srNo")}</th>
                                        <th>{t("tableColumnName.shopName")}</th>

                                        {hasPermission(["shopper-edit"]) && (
                                          <th>{t("tableColumnName.status")}</th>
                                        )}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {account.shops &&
                                      account.shops.length > 0 ? (
                                        account.shops.map((shop, shopIndex) => (
                                          <tr key={shop.id}>
                                            <td>{shopIndex + 1}</td>
                                            <td>{shop.name}</td>
                                            {hasPermission([
                                              "shopper-edit",
                                            ]) && (
                                              <td className="d-flex justify-content-start">
                                                <UnlimitToggle
                                                  isActive={
                                                    shop.is_shop_associated_with_shopper
                                                  }
                                                  id={`shop_status_${account.id}_${shop.id}`}
                                                  toggleActive={(checked) =>
                                                    toggleShopStatus(
                                                      account.id,
                                                      shop.id,
                                                      checked
                                                    )
                                                  }
                                                  disabled={isToggleLoading(
                                                    "shop",
                                                    account.id,
                                                    shop.id
                                                  )}
                                                />
                                              </td>
                                            )}
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td
                                            colSpan={3}
                                            className="text-center"
                                          >
                                            {t("general.noData")}
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </Panel>
                            </Collapse>
                          ))
                        ) : (
                          <div className="alert alert-info">
                            {t("shopper.noAssociateAccounts")}
                          </div>
                        )}
                      </Panel>

                      <Panel
                        header={t("shopper.salesOrderHistory")}
                        key="salesHistory"
                      >
                        <UnlimitTable
                          pagination={paginationParams.pagination}
                          columns={columns}
                          dataSource={salesOrderHistory}
                          rowKey={(record) => record.id}
                          loading={loading}
                          onChange={handleTableChange}
                        />
                      </Panel>
                    </Collapse>
                  </div>
                </div>
              </>
            )
          )}
        </div>
      </div>
    </>
  );
}
